package com.writerslab.office

import io.ktor.client.*
import io.ktor.client.engine.js.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.http.content.*
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response
import org.w3c.files.File
import org.w3c.xhr.FormData

private val json = Json { ignoreUnknownKeys = true }

object ApiClient {
    private val client = HttpClient(Js) { expectSuccess = false }

    private fun base() = Auth.apiBase

    private suspend fun get(path: String): HttpResponse {
        if (NO_AUTH) return client.get("${base()}$path")
        val token = Auth.getAccessToken() ?: error("Not authenticated")
        val resp = client.get("${base()}$path") {
            header(HttpHeaders.Authorization, "Bearer $token")
        }
        if (resp.status == HttpStatusCode.Unauthorized) {
            if (Auth.refreshAccessToken()) {
                val fresh = Auth.getAccessToken() ?: error("Token gone after refresh")
                return client.get("${base()}$path") {
                    header(HttpHeaders.Authorization, "Bearer $fresh")
                }
            }
            Auth.clearTokens()
            error("Session expired")
        }
        return resp
    }

    private suspend fun postJson(path: String, bodyJson: String): HttpResponse {
        if (NO_AUTH) return client.post("${base()}$path") {
            setBody(TextContent(bodyJson, ContentType.Application.Json))
        }
        val token = Auth.getAccessToken() ?: error("Not authenticated")
        val resp = client.post("${base()}$path") {
            header(HttpHeaders.Authorization, "Bearer $token")
            setBody(TextContent(bodyJson, ContentType.Application.Json))
        }
        if (resp.status == HttpStatusCode.Unauthorized) {
            if (Auth.refreshAccessToken()) {
                val fresh = Auth.getAccessToken() ?: error("Token gone after refresh")
                return client.post("${base()}$path") {
                    header(HttpHeaders.Authorization, "Bearer $fresh")
                    setBody(TextContent(bodyJson, ContentType.Application.Json))
                }
            }
            Auth.clearTokens()
            error("Session expired")
        }
        return resp
    }

    private suspend fun putJson(path: String, bodyJson: String): HttpResponse {
        if (NO_AUTH) return client.put("${base()}$path") {
            setBody(TextContent(bodyJson, ContentType.Application.Json))
        }
        val token = Auth.getAccessToken() ?: error("Not authenticated")
        val resp = client.put("${base()}$path") {
            header(HttpHeaders.Authorization, "Bearer $token")
            setBody(TextContent(bodyJson, ContentType.Application.Json))
        }
        if (resp.status == HttpStatusCode.Unauthorized) {
            if (Auth.refreshAccessToken()) {
                val fresh = Auth.getAccessToken() ?: error("Token gone after refresh")
                return client.put("${base()}$path") {
                    header(HttpHeaders.Authorization, "Bearer $fresh")
                    setBody(TextContent(bodyJson, ContentType.Application.Json))
                }
            }
            Auth.clearTokens()
            error("Session expired")
        }
        return resp
    }

    private suspend fun deleteReq(path: String): HttpResponse {
        if (NO_AUTH) return client.delete("${base()}$path")
        val token = Auth.getAccessToken() ?: error("Not authenticated")
        val resp = client.delete("${base()}$path") {
            header(HttpHeaders.Authorization, "Bearer $token")
        }
        if (resp.status == HttpStatusCode.Unauthorized) {
            if (Auth.refreshAccessToken()) {
                val fresh = Auth.getAccessToken() ?: error("Token gone after refresh")
                return client.delete("${base()}$path") {
                    header(HttpHeaders.Authorization, "Bearer $fresh")
                }
            }
            Auth.clearTokens()
            error("Session expired")
        }
        return resp
    }

    suspend fun getStats(): OfficeStats = json.decodeFromString(get("/api/office/stats").bodyAsText())

    suspend fun getCodes(): List<OfficeCode> = json.decodeFromString(get("/api/office/codes").bodyAsText())

    suspend fun generateCodes(req: GenerateCodesRequest): GenerateCodesResponse {
        val resp = postJson("/api/office/codes/generate", json.encodeToString(req))
        return json.decodeFromString(resp.bodyAsText())
    }

    suspend fun getUsers(): List<OfficeUser> = json.decodeFromString(get("/api/office/users").bodyAsText())

    suspend fun getAnalytics(period: String): List<OfficeAnalyticsBucket> =
        json.decodeFromString(get("/api/office/analytics?period=$period").bodyAsText())

    suspend fun getServices(period: String): List<OfficeServiceBucket> =
        json.decodeFromString(get("/api/office/services?period=$period").bodyAsText())

    suspend fun getBooks(includeAnon: Boolean = false): List<OfficeBook> =
        json.decodeFromString(get("/api/office/books?includeAnon=$includeAnon").bodyAsText())

    /**
     * A URL the browser can download [path] (relative to /api/office) from by
     * itself: it carries a one-time ticket instead of the Bearer token, so the
     * file streams to disk through the browser's download manager rather than
     * being buffered in the page.
     */
    suspend fun downloadUrl(path: String): String {
        val url = "${base()}/api/office$path"
        if (NO_AUTH) return url
        val resp = postJson("/api/office/download-tickets", json.encodeToString(DownloadTicketRequest(path)))
        if (!resp.status.isSuccess()) error("HTTP ${resp.status.value}")
        val ticket = json.decodeFromString<DownloadTicketResponse>(resp.bodyAsText()).ticket
        return url + (if ('?' in path) "&" else "?") + "ticket=$ticket"
    }

    // ── Blog ────────────────────────────────────────────────────────────

    suspend fun getBlogList(): List<BlogSummary> =
        json.decodeFromString(get("/api/office/blog").bodyAsText())

    suspend fun getBlogArticle(id: Long): BlogArticle =
        json.decodeFromString(get("/api/office/blog/$id").bodyAsText())

    suspend fun createBlog(req: BlogUpsertRequest): BlogArticle =
        decodeBlogOrThrow(postJson("/api/office/blog", json.encodeToString(req)))

    suspend fun updateBlog(id: Long, req: BlogUpsertRequest): BlogArticle =
        decodeBlogOrThrow(putJson("/api/office/blog/$id", json.encodeToString(req)))

    suspend fun publishBlog(id: Long): BlogArticle =
        decodeBlogOrThrow(postJson("/api/office/blog/$id/publish", ""))

    suspend fun hideBlog(id: Long): BlogArticle =
        decodeBlogOrThrow(postJson("/api/office/blog/$id/hide", ""))

    suspend fun deleteBlog(id: Long) {
        val resp = deleteReq("/api/office/blog/$id")
        if (!resp.status.isSuccess()) error(parseError(resp.bodyAsText()))
    }

    /** Server-rendered preview page (full HTML document) for the editor iframe. */
    suspend fun previewBlog(markdown: String, language: String): String {
        val resp = postJson("/api/office/blog/preview", json.encodeToString(BlogPreviewRequest(markdown, language)))
        val body = resp.bodyAsText()
        if (!resp.status.isSuccess()) error(parseError(body))
        return body
    }

    /** Upload an image via native fetch (multipart is simplest with a File). */
    suspend fun uploadBlogImage(file: File): BlogImageUploadResponse {
        if (NO_AUTH) {
            val resp = fetchUpload(file, null)
            val body = resp.text().await()
            if (!resp.ok) error(parseError(body))
            return json.decodeFromString(body)
        }
        val token = Auth.getAccessToken() ?: error("Not authenticated")
        var resp = fetchUpload(file, token)
        if (resp.status.toInt() == 401) {
            if (Auth.refreshAccessToken()) {
                val fresh = Auth.getAccessToken() ?: error("Token gone after refresh")
                resp = fetchUpload(file, fresh)
            } else {
                Auth.clearTokens()
                error("Session expired")
            }
        }
        val body = resp.text().await()
        if (!resp.ok) error(parseError(body))
        return json.decodeFromString(body)
    }

    private suspend fun fetchUpload(file: File, token: String?): Response {
        val form = FormData()
        form.append("file", file, file.name)
        val headers: dynamic = js("({})")
        if (token != null) headers["Authorization"] = "Bearer $token"
        return window.fetch(
            "${base()}/api/office/blog/images",
            RequestInit(method = "POST", headers = headers, body = form),
        ).await()
    }

    private suspend fun decodeBlogOrThrow(resp: HttpResponse): BlogArticle {
        val body = resp.bodyAsText()
        if (!resp.status.isSuccess()) error(parseError(body))
        return json.decodeFromString(body)
    }

    private fun parseError(body: String): String =
        runCatching {
            json.parseToJsonElement(body).jsonObject["error"]?.jsonPrimitive?.content
        }.getOrNull() ?: "Ошибка запроса"
}
